// Ingest. The critical path, and the largest unresolved dependency in the build.
//
// There is no public API for this festival's set times, so the reliable option is
// a structured paste with a parser and a review step in front of it. Two shapes
// are accepted, because both are what people actually have on the clipboard:
//
//   delimited  a header row naming columns, then rows. Comma or tab.
//   grouped    a day heading, a stage heading, then time ranges and artist names.
//
// The parser never writes. It returns a change set against whatever is already
// stored, and the review screen is what commits it. A re-import therefore produces
// a diff rather than a wholesale replacement, and every plan touched by a changed
// set is marked stale rather than left quietly wrong.

import type {
  Artist,
  Day,
  Festival,
  FestivalSet,
  Id,
  IngestChange,
  IngestResult,
  Stage,
} from './types.ts';
import { VENUE_TIMEZONE, dayIdFor, festivalTimestamp } from './time.ts';

const TIME_RANGE = /(\d{1,2}):(\d{2})\s*(?:-|to|until|\u2013)\s*(\d{1,2}):(\d{2})/i;
const DELIMITERS = [',', '\t', '|', ';'];

export interface IngestOptions {
  festival: Festival;
  days: Day[];
  stages: Stage[];
  existingArtists: Artist[];
  existingSets: FestivalSet[];
  /** Sets ending before this hour are filed under the previous festival day. */
  rolloverHour?: number;
}

interface ParsedRow {
  dayLabel?: string;
  stageName: string;
  artistName: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  billingNote?: string;
}

/** Parses a pasted grid into rows without touching anything stored. */
export function parsePaste(text: string, opts: IngestOptions): { rows: ParsedRow[]; warnings: string[] } {
  const warnings: string[] = [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return { rows: [], warnings: ['Nothing to read in that paste.'] };

  const delimiter = pickDelimiter(lines);
  if (delimiter) return parseDelimited(lines, delimiter, opts, warnings);
  return parseGrouped(lines, opts, warnings);
}

// Splits one row on the delimiter, honoring double quotes. An artist billed as
// "Someone, Someone Else" is one cell; a plain split makes it two and shifts
// every column after it.
function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      cells.push(cell);
      cell = '';
    } else cell += ch;
  }
  cells.push(cell);
  return cells;
}

function pickDelimiter(lines: string[]): string | null {
  const header = lines[0].toLowerCase();
  for (const d of DELIMITERS) {
    const cells = header.split(d).map((c) => c.trim());
    if (cells.length < 3) continue;
    const named = cells.filter((c) => /artist|act|stage|start|end|time|day/.test(c));
    if (named.length >= 3) return d;
  }
  return null;
}

function parseDelimited(
  lines: string[],
  delimiter: string,
  opts: IngestOptions,
  warnings: string[],
): { rows: ParsedRow[]; warnings: string[] } {
  const header = splitRow(lines[0], delimiter).map((c) => c.trim().toLowerCase());
  const col = (...names: string[]) => header.findIndex((h) => names.some((n) => h.includes(n)));
  const iArtist = col('artist', 'act', 'name');
  const iStage = col('stage', 'venue');
  const iStart = col('start');
  const iEnd = col('end', 'finish');
  const iTime = col('time', 'slot');
  const iDay = col('day', 'date');
  const iNote = col('note', 'billing');

  if (iArtist < 0 || iStage < 0) {
    warnings.push('That paste needs at least an artist column and a stage column.');
    return { rows: [], warnings };
  }

  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitRow(lines[i], delimiter).map((c) => c.trim());
    const artistName = cells[iArtist];
    const stageName = cells[iStage];
    if (!artistName || !stageName) continue;

    let times: [number, number, number, number] | null = null;
    if (iStart >= 0 && iEnd >= 0) times = readPair(cells[iStart], cells[iEnd]);
    if (!times && iTime >= 0) times = readRange(cells[iTime]);
    if (!times) {
      warnings.push(`Line ${i + 1} has no readable time: ${lines[i].slice(0, 60)}`);
      continue;
    }

    rows.push({
      dayLabel: iDay >= 0 ? cells[iDay] : undefined,
      stageName,
      artistName,
      startHour: times[0],
      startMinute: times[1],
      endHour: times[2],
      endMinute: times[3],
      billingNote: iNote >= 0 ? cells[iNote] || undefined : undefined,
    });
  }
  void opts;
  return { rows, warnings };
}

function parseGrouped(
  lines: string[],
  opts: IngestOptions,
  warnings: string[],
): { rows: ParsedRow[]; warnings: string[] } {
  const stageNames = opts.stages.map((s) => s.name.toLowerCase());
  const dayLabels = opts.days.map((d) => d.label.toLowerCase());
  const rows: ParsedRow[] = [];
  let currentStage: string | null = null;
  let currentDay: string | undefined;

  for (const line of lines) {
    const lower = line.toLowerCase();
    const asDay = dayLabels.findIndex((d) => lower === d || lower.startsWith(d));
    if (asDay >= 0 && !TIME_RANGE.test(line)) {
      currentDay = opts.days[asDay].label;
      continue;
    }
    const asStage = stageNames.findIndex((s) => lower === s);
    if (asStage >= 0) {
      currentStage = opts.stages[asStage].name;
      continue;
    }
    const m = line.match(TIME_RANGE);
    if (!m) {
      // An unrecognized bare line is treated as a stage heading, which is how most
      // pasted grids separate their columns.
      if (line.length < 48 && !/\d/.test(line)) currentStage = line;
      continue;
    }
    if (!currentStage) {
      warnings.push(`A set appeared before any stage heading: ${line.slice(0, 60)}`);
      continue;
    }
    const artistName = line.replace(m[0], '').replace(/^[\s\-\u2013:|]+|[\s\-\u2013:|]+$/g, '').trim();
    if (!artistName) continue;
    rows.push({
      dayLabel: currentDay,
      stageName: currentStage,
      artistName,
      startHour: Number(m[1]),
      startMinute: Number(m[2]),
      endHour: Number(m[3]),
      endMinute: Number(m[4]),
    });
  }
  return { rows, warnings };
}

function readPair(a: string, b: string): [number, number, number, number] | null {
  const pa = readClock(a);
  const pb = readClock(b);
  return pa && pb ? [pa[0], pa[1], pb[0], pb[1]] : null;
}

function readRange(s: string): [number, number, number, number] | null {
  const m = s.match(TIME_RANGE);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])] : null;
}

function readClock(s: string): [number, number] | null {
  const m = s.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const suffix = m[3]?.toLowerCase();
  if (suffix === 'pm' && hour < 12) hour += 12;
  if (suffix === 'am' && hour === 12) hour = 0;
  return [hour, minute];
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Turns parsed rows into sets, resolving stages, artists and festival days. */
export function buildSets(rows: ParsedRow[], opts: IngestOptions): {
  artists: Artist[];
  sets: FestivalSet[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const artists = new Map<Id, Artist>(opts.existingArtists.map((a) => [a.id, a]));
  const aliasIndex = new Map<string, Id>();
  for (const a of opts.existingArtists) {
    aliasIndex.set(a.name.toLowerCase(), a.id);
    for (const alias of a.aliases) aliasIndex.set(alias.toLowerCase(), a.id);
  }

  const stageByName = new Map(opts.stages.map((s) => [s.name.toLowerCase(), s]));
  const sets: FestivalSet[] = [];

  for (const row of rows) {
    const stage = stageByName.get(row.stageName.toLowerCase());
    if (!stage) {
      warnings.push(`No stage matches "${row.stageName}". Add it before importing.`);
      continue;
    }

    let artistId = aliasIndex.get(row.artistName.toLowerCase());
    if (!artistId) {
      artistId = `artist-${slug(row.artistName)}`;
      artists.set(artistId, { id: artistId, name: row.artistName, genreTags: [], aliases: [] });
      aliasIndex.set(row.artistName.toLowerCase(), artistId);
    }

    const day = row.dayLabel
      ? opts.days.find((d) => d.label.toLowerCase() === row.dayLabel!.toLowerCase())
      : opts.days[0];
    if (!day) {
      warnings.push(`No festival day matches "${row.dayLabel}".`);
      continue;
    }

    // Both instants are built in the venue's zone. A time that a daylight-saving
    // jump makes impossible or ambiguous is a warning and a skipped row rather
    // than a date the runtime silently rolls over.
    const hhmm = (h: number, min: number) =>
      `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

    let startTs: number;
    let endTs: number;
    try {
      startTs = festivalTimestamp(
        day.calendarDate,
        hhmm(row.startHour, row.startMinute),
        VENUE_TIMEZONE,
        day.rolloverHour,
      );
      endTs = festivalTimestamp(
        day.calendarDate,
        hhmm(row.endHour, row.endMinute),
        VENUE_TIMEZONE,
        day.rolloverHour,
      );
    } catch {
      warnings.push(`"${row.artistName}" has a time that does not exist on ${day.label}.`);
      continue;
    }
    if (endTs <= startTs) endTs += 24 * 60 * 60 * 1000;

    const resolvedDay = dayIdFor(opts.days, startTs) ?? day.id;
    sets.push({
      id: `set-${slug(row.artistName)}-${startTs}`,
      artistId,
      stageId: stage.id,
      dayId: resolvedDay,
      startTs,
      endTs,
      billingNote: row.billingNote,
    });
  }

  return { artists: [...artists.values()], sets, warnings };
}

/** Compares incoming sets against stored ones and returns the change set. */
export function diffSets(
  existing: FestivalSet[],
  incoming: FestivalSet[],
  artists: Artist[],
): IngestChange[] {
  const nameOf = (artistId: Id) => artists.find((a) => a.id === artistId)?.name ?? artistId;
  const key = (s: FestivalSet) => `${s.artistId}|${s.dayId}|${s.stageId}`;
  const before = new Map(existing.map((s) => [key(s), s]));
  const after = new Map(incoming.map((s) => [key(s), s]));
  const changes: IngestChange[] = [];

  for (const [k, s] of after) {
    const prev = before.get(k);
    if (!prev) {
      changes.push({ kind: 'added', setId: s.id, artistName: nameOf(s.artistId), after: s });
      continue;
    }
    const fields: string[] = [];
    if (prev.startTs !== s.startTs) fields.push('startTs');
    if (prev.endTs !== s.endTs) fields.push('endTs');
    if (prev.stageId !== s.stageId) fields.push('stageId');
    if (prev.billingNote !== s.billingNote) fields.push('billingNote');
    if (fields.length > 0) {
      changes.push({ kind: 'changed', setId: prev.id, artistName: nameOf(s.artistId), before: prev, after: s, fields });
    }
  }

  // A parse that produced nothing is a failed read, not an announcement that
  // the bill is empty. Removals are only proposed when something was parsed.
  if (incoming.length === 0) return changes;

  // A paste covering one day does not speak for the days it never mentions. A
  // removal is only proposed inside the days the import actually carried.
  const importedDays = new Set(incoming.map((s) => s.dayId));
  for (const [k, s] of before) {
    if (!after.has(k) && importedDays.has(s.dayId)) {
      changes.push({ kind: 'removed', setId: s.id, artistName: nameOf(s.artistId), before: s });
    }
  }

  return changes;
}

/** Runs a paste through parse, build and diff without committing anything. */
export function ingestPaste(text: string, opts: IngestOptions): IngestResult {
  const parsed = parsePaste(text, opts);
  const built = buildSets(parsed.rows, opts);
  const changes = diffSets(opts.existingSets, built.sets, built.artists);
  return {
    festival: opts.festival,
    days: opts.days,
    stages: opts.stages,
    artists: built.artists,
    sets: built.sets,
    changes,
    warnings: [...parsed.warnings, ...built.warnings],
  };
}

/** Returns the ids of plans a change set invalidates. */
export function stalePlanIds(
  changes: IngestChange[],
  plans: { id: Id; blocks: { setId?: Id }[] }[],
): Id[] {
  const touched = new Set(changes.map((c) => c.setId));
  return plans.filter((p) => p.blocks.some((b) => b.setId && touched.has(b.setId))).map((p) => p.id);
}
